import { useListEnvironmentInventory } from "@/queries/environment-queries";
import { useProjectListPage } from "@/queries/project-queries";
import { useListRunners, type PlainRunner } from "@/queries/runner-queries";
import type { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { useMemo } from "react";

export type RunnerWithUsage = {
    runner: PlainRunner;
    projectCount: number;
    environmentCount: number;
    hasMoreEnvironments: boolean;
};

/**
 * Hook to fetch all runners that use the canonical domain (e.g., app.gitpod.io)
 */
export const useCanonicalDomainRunners = (canonicalDomain: string) => {
    const { runners, isLoading: isLoadingRunners } = useListRunners({});

    // Filter runners that use the canonical domain
    const canonicalDomainRunners = useMemo(() => {
        if (!runners) return [];

        return runners.filter((runner) => {
            const apiEndpoint = runner.status?.additionalInfo?.find((info) => info.key === "apiEndpoint")?.value;

            if (!apiEndpoint) return false;

            try {
                const url = new URL(apiEndpoint);
                return url.hostname === canonicalDomain;
            } catch {
                return false;
            }
        });
    }, [runners, canonicalDomain]);

    return {
        runners: canonicalDomainRunners,
        isLoading: isLoadingRunners,
    };
};

/**
 * Hook to get usage statistics for a runner, identified by either
 * a specific runner ID or a runner kind (e.g. local runners).
 */
export const useRunnerUsage = ({
    runnerId,
    runnerKinds,
}: {
    runnerId?: string;
    runnerKinds?: RunnerKind[];
}) => {
    const { data: projectsData, isPending: isPendingProjects } = useProjectListPage({
        runnerIds: runnerId ? [runnerId] : undefined,
        runnerKinds,
        page: 0,
    });

    const environmentQuery = runnerKinds ? { runnerKind: runnerKinds[0] } : { runnerID: runnerId };

    const {
        data: environmentsData,
        hasNextPage: hasNextPageEnvironments,
        isPending: isPendingEnvironments,
    } = useListEnvironmentInventory(environmentQuery);

    const numEnvironments = useMemo(() => {
        return environmentsData?.pages.flatMap((page) => page.environments).length ?? 0;
    }, [environmentsData]);

    return {
        projectCount: projectsData?.totalCount ?? 0,
        environmentCount: numEnvironments,
        hasMoreEnvironments: hasNextPageEnvironments ?? false,
        isLoading: isPendingProjects || isPendingEnvironments,
    };
};
